import { toast } from 'react-toastify';
import type React from 'react';

import type {
  AudienciaPresencialItem,
  AudienciaRemotaItem,
  RelatorioCivelFormData,
} from '../relatorio-civel-page.models';
import type { QuantidadeLinhasState } from '../steps/relatorio-civel-step-props';

type UseAudienciasParams = {
  formData: RelatorioCivelFormData;
  setFormData: React.Dispatch<React.SetStateAction<RelatorioCivelFormData>>;
  quantidadeLinhas: QuantidadeLinhasState;

  // view
  isViewMode: boolean;

  // inputs
  novaAudienciaPresencial: string;
  setNovaAudienciaPresencial: React.Dispatch<React.SetStateAction<string>>;
  novoNumeroProtocolo: string;
  setNovoNumeroProtocolo: React.Dispatch<React.SetStateAction<string>>;
  novaDataPresencial: string;
  setNovaDataPresencial: React.Dispatch<React.SetStateAction<string>>;

  novaAudienciaRemota: string;
  setNovaAudienciaRemota: React.Dispatch<React.SetStateAction<string>>;
  novoNumeroProcessoRemota: string;
  setNovoNumeroProcessoRemota: React.Dispatch<React.SetStateAction<string>>;
  novaDataRemota: string;
  setNovaDataRemota: React.Dispatch<React.SetStateAction<string>>;

  // edição presencial
  editandoAudienciaPresencial: string | null;
  setEditandoAudienciaPresencial: React.Dispatch<React.SetStateAction<string | null>>;
  valorEdicaoAudienciaPresencial: string;
  setValorEdicaoAudienciaPresencial: React.Dispatch<React.SetStateAction<string>>;
  valorEdicaoNumeroProtocolo: string;
  setValorEdicaoNumeroProtocolo: React.Dispatch<React.SetStateAction<string>>;
  valorEdicaoDataPresencial: string;
  setValorEdicaoDataPresencial: React.Dispatch<React.SetStateAction<string>>;

  // edição remota
  editandoAudienciaRemota: string | null;
  setEditandoAudienciaRemota: React.Dispatch<React.SetStateAction<string | null>>;
  valorEdicaoAudienciaRemota: string;
  setValorEdicaoAudienciaRemota: React.Dispatch<React.SetStateAction<string>>;
  valorEdicaoNumeroProcessoRemota: string;
  setValorEdicaoNumeroProcessoRemota: React.Dispatch<React.SetStateAction<string>>;
  valorEdicaoDataRemota: string;
  setValorEdicaoDataRemota: React.Dispatch<React.SetStateAction<string>>;
};

export function useAudiencias(params: UseAudienciasParams) {
  const {
    setFormData,
    quantidadeLinhas,
    isViewMode,

    novaAudienciaPresencial,
    setNovaAudienciaPresencial,
    novoNumeroProtocolo,
    setNovoNumeroProtocolo,
    novaDataPresencial,
    setNovaDataPresencial,

    novaAudienciaRemota,
    setNovaAudienciaRemota,
    novoNumeroProcessoRemota,
    setNovoNumeroProcessoRemota,
    novaDataRemota,
    setNovaDataRemota,

    setEditandoAudienciaPresencial,
    valorEdicaoAudienciaPresencial,
    setValorEdicaoAudienciaPresencial,
    valorEdicaoNumeroProtocolo,
    setValorEdicaoNumeroProtocolo,
    valorEdicaoDataPresencial,
    setValorEdicaoDataPresencial,

    setEditandoAudienciaRemota,
    valorEdicaoAudienciaRemota,
    setValorEdicaoAudienciaRemota,
    valorEdicaoNumeroProcessoRemota,
    setValorEdicaoNumeroProcessoRemota,
    valorEdicaoDataRemota,
    setValorEdicaoDataRemota,
  } = params;

  const adicionarAudienciaPresencial = () => {
    if (isViewMode) return;

    if (!novaAudienciaPresencial.trim()) {
      toast.warning('Selecione o tipo de audiência presencial.');
      return;
    }
    if (!novoNumeroProtocolo.trim()) {
      toast.warning('Informe o número do processo.');
      return;
    }
    if (!novaDataPresencial) {
      toast.warning('Informe a data da audiência.');
      return;
    }

    const novoItem: AudienciaPresencialItem = {
      id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      audienciaPresencial: novaAudienciaPresencial.trim(),
      numeroProtocolo: novoNumeroProtocolo.trim(),
      data: new Date(novaDataPresencial),
    };

    setFormData((prev) => ({
      ...prev,
      audiencias: {
        ...prev.audiencias,
        presenciais: [...prev.audiencias.presenciais, novoItem],
      },
    }));

    setNovaAudienciaPresencial('');
    setNovoNumeroProtocolo('');
    setNovaDataPresencial('');
    toast.success('Audiência presencial adicionada com sucesso!');
  };

  const removerAudienciaPresencial = (id: string) => {
    if (isViewMode) return;
    setFormData((prev) => ({
      ...prev,
      audiencias: {
        ...prev.audiencias,
        presenciais: prev.audiencias.presenciais.filter((item) => item.id !== id),
      },
    }));
    toast.success('Audiência presencial removida com sucesso!');
  };

  const adicionarMultiplasLinhasAudienciasPresenciais = () => {
    if (isViewMode) return;

    const quantidade = quantidadeLinhas.audienciasPresenciais;
    if (quantidade < 1) {
      toast.warning('A quantidade mínima é 1.');
      return;
    }

    const novosItens: AudienciaPresencialItem[] = [];
    for (let i = 0; i < quantidade; i++) {
      novosItens.push({
        id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}-${i}`,
        audienciaPresencial: '',
        numeroProtocolo: '',
        data: new Date(),
      });
    }

    setFormData((prev) => ({
      ...prev,
      audiencias: {
        ...prev.audiencias,
        presenciais: [...prev.audiencias.presenciais, ...novosItens],
      },
    }));

    toast.success(`${quantidade} linha(s) adicionada(s) com sucesso!`);
  };

  const adicionarAudienciaRemota = () => {
    if (isViewMode) return;

    if (!novaAudienciaRemota.trim()) {
      toast.warning('Selecione o tipo de audiência remota.');
      return;
    }
    if (!novoNumeroProcessoRemota.trim()) {
      toast.warning('Informe o número do processo.');
      return;
    }
    if (!novaDataRemota) {
      toast.warning('Informe a data da audiência.');
      return;
    }

    const novoItem: AudienciaRemotaItem = {
      id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      audienciaRemota: novaAudienciaRemota.trim(),
      numeroProcesso: novoNumeroProcessoRemota.trim(),
      data: new Date(novaDataRemota),
    };

    setFormData((prev) => ({
      ...prev,
      audiencias: {
        ...prev.audiencias,
        remotas: [...prev.audiencias.remotas, novoItem],
      },
    }));

    setNovaAudienciaRemota('');
    setNovoNumeroProcessoRemota('');
    setNovaDataRemota('');
    toast.success('Audiência remota adicionada com sucesso!');
  };

  const removerAudienciaRemota = (id: string) => {
    if (isViewMode) return;
    setFormData((prev) => ({
      ...prev,
      audiencias: {
        ...prev.audiencias,
        remotas: prev.audiencias.remotas.filter((item) => item.id !== id),
      },
    }));
    toast.success('Audiência remota removida com sucesso!');
  };

  const adicionarMultiplasLinhasAudienciasRemotas = () => {
    if (isViewMode) return;

    const quantidade = quantidadeLinhas.audienciasRemotas;
    if (quantidade < 1) {
      toast.warning('A quantidade mínima é 1.');
      return;
    }

    const novosItens: AudienciaRemotaItem[] = [];
    for (let i = 0; i < quantidade; i++) {
      novosItens.push({
        id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}-${i}`,
        audienciaRemota: '',
        numeroProcesso: '',
        data: new Date(),
      });
    }

    setFormData((prev) => ({
      ...prev,
      audiencias: {
        ...prev.audiencias,
        remotas: [...prev.audiencias.remotas, ...novosItens],
      },
    }));

    toast.success(`${quantidade} linha(s) adicionada(s) com sucesso!`);
  };

  const iniciarEdicaoAudienciaPresencial = (item: AudienciaPresencialItem) => {
    if (isViewMode) return;
    setEditandoAudienciaPresencial(item.id);
    setValorEdicaoAudienciaPresencial(item.audienciaPresencial);
    setValorEdicaoNumeroProtocolo(item.numeroProtocolo);
    setValorEdicaoDataPresencial(new Date(item.data).toISOString().split('T')[0]);
  };

  const cancelarEdicaoAudienciaPresencial = () => {
    setEditandoAudienciaPresencial(null);
    setValorEdicaoAudienciaPresencial('');
    setValorEdicaoNumeroProtocolo('');
    setValorEdicaoDataPresencial('');
  };

  const salvarEdicaoAudienciaPresencial = (id: string) => {
    if (isViewMode) return;

    if (!valorEdicaoAudienciaPresencial.trim()) {
      toast.warning('Selecione o tipo de audiência presencial.');
      return;
    }
    if (!valorEdicaoNumeroProtocolo.trim()) {
      toast.warning('Informe o número do processo.');
      return;
    }
    if (!valorEdicaoDataPresencial) {
      toast.warning('Informe a data da audiência.');
      return;
    }

    setFormData((prev) => ({
      ...prev,
      audiencias: {
        ...prev.audiencias,
        presenciais: prev.audiencias.presenciais.map((item) =>
          item.id === id
            ? {
              ...item,
              audienciaPresencial: valorEdicaoAudienciaPresencial.trim(),
              numeroProtocolo: valorEdicaoNumeroProtocolo.trim(),
              data: new Date(valorEdicaoDataPresencial),
            }
            : item,
        ),
      },
    }));

    cancelarEdicaoAudienciaPresencial();
    toast.success('Audiência presencial atualizada com sucesso!');
  };

  const iniciarEdicaoAudienciaRemota = (item: AudienciaRemotaItem) => {
    if (isViewMode) return;
    setEditandoAudienciaRemota(item.id);
    setValorEdicaoAudienciaRemota(item.audienciaRemota);
    setValorEdicaoNumeroProcessoRemota(item.numeroProcesso);
    setValorEdicaoDataRemota(new Date(item.data).toISOString().split('T')[0]);
  };

  const cancelarEdicaoAudienciaRemota = () => {
    setEditandoAudienciaRemota(null);
    setValorEdicaoAudienciaRemota('');
    setValorEdicaoNumeroProcessoRemota('');
    setValorEdicaoDataRemota('');
  };

  const salvarEdicaoAudienciaRemota = (id: string) => {
    if (isViewMode) return;

    if (!valorEdicaoAudienciaRemota.trim()) {
      toast.warning('Selecione o tipo de audiência remota.');
      return;
    }
    if (!valorEdicaoNumeroProcessoRemota.trim()) {
      toast.warning('Informe o número do processo.');
      return;
    }
    if (!valorEdicaoDataRemota) {
      toast.warning('Informe a data da audiência.');
      return;
    }

    setFormData((prev) => ({
      ...prev,
      audiencias: {
        ...prev.audiencias,
        remotas: prev.audiencias.remotas.map((item) =>
          item.id === id
            ? {
              ...item,
              audienciaRemota: valorEdicaoAudienciaRemota.trim(),
              numeroProcesso: valorEdicaoNumeroProcessoRemota.trim(),
              data: new Date(valorEdicaoDataRemota),
            }
            : item,
        ),
      },
    }));

    cancelarEdicaoAudienciaRemota();
    toast.success('Audiência remota atualizada com sucesso!');
  };

  return {
    adicionarAudienciaPresencial,
    removerAudienciaPresencial,
    adicionarMultiplasLinhasAudienciasPresenciais,
    iniciarEdicaoAudienciaPresencial,
    salvarEdicaoAudienciaPresencial,
    cancelarEdicaoAudienciaPresencial,

    adicionarAudienciaRemota,
    removerAudienciaRemota,
    adicionarMultiplasLinhasAudienciasRemotas,
    iniciarEdicaoAudienciaRemota,
    salvarEdicaoAudienciaRemota,
    cancelarEdicaoAudienciaRemota,
  };
}

